/**
 * Raydium LaunchLab Integration
 *
 * For tokens launched via Raydium's LaunchLab program.
 * Uses similar bonding curve mechanics.
 */

import {
  Connection,
  PublicKey,
  TransactionInstruction,
  SystemProgram,
} from '@solana/web3.js';
import {
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddress,
} from '@solana/spl-token';

// Raydium LaunchLab Program ID
export const RAYDIUM_LAUNCHLAB_PROGRAM_ID = new PublicKey('LanMV9sAd7wArD4vJFi2qDdfnVhFxYSUg6eADduJ3uj');

/**
 * Raydium LaunchLab pool state
 */
export interface LaunchLabPoolState {
  tokenMint: PublicKey;
  quoteMint: PublicKey; // Usually SOL/WSOL
  tokenReserve: bigint;
  quoteReserve: bigint;
  totalSupply: bigint;
  graduated: boolean;
  createdAt: number;
}

/**
 * Derive the pool PDA for a LaunchLab token
 */
export function getPoolPDA(mint: PublicKey): PublicKey {
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('pool'), mint.toBuffer()],
    RAYDIUM_LAUNCHLAB_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the pool token vault
 */
export function getPoolTokenVault(mint: PublicKey): PublicKey {
  const pool = getPoolPDA(mint);
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('vault'), pool.toBuffer(), mint.toBuffer()],
    RAYDIUM_LAUNCHLAB_PROGRAM_ID
  );
  return pda;
}

/**
 * Derive the pool SOL vault
 */
export function getPoolSolVault(mint: PublicKey): PublicKey {
  const pool = getPoolPDA(mint);
  const [pda] = PublicKey.findProgramAddressSync(
    [Buffer.from('sol_vault'), pool.toBuffer()],
    RAYDIUM_LAUNCHLAB_PROGRAM_ID
  );
  return pda;
}

/**
 * Fetch pool state from chain
 */
export async function getPoolState(
  connection: Connection,
  mint: PublicKey
): Promise<LaunchLabPoolState | null> {
  const pool = getPoolPDA(mint);
  const accountInfo = await connection.getAccountInfo(pool);

  if (!accountInfo) {
    return null;
  }

  // Parse pool data - layout varies by Raydium version
  // This is a simplified parser
  const data = accountInfo.data;

  if (data.length < 100) {
    return null;
  }

  try {
    return {
      tokenMint: new PublicKey(data.slice(8, 40)),
      quoteMint: new PublicKey(data.slice(40, 72)),
      tokenReserve: data.readBigUInt64LE(72),
      quoteReserve: data.readBigUInt64LE(80),
      totalSupply: data.readBigUInt64LE(88),
      graduated: data[96] === 1,
      createdAt: Number(data.readBigUInt64LE(97)),
    };
  } catch {
    return null;
  }
}

/**
 * Calculate buy output using constant product formula
 */
export function calculateBuyOutput(
  solAmount: bigint,
  state: LaunchLabPoolState
): bigint {
  if (state.graduated) {
    throw new Error('Pool has graduated - use Raydium AMM or Jupiter');
  }

  // x * y = k formula
  const numerator = solAmount * state.tokenReserve;
  const denominator = state.quoteReserve + solAmount;

  return numerator / denominator;
}

/**
 * Calculate sell output
 */
export function calculateSellOutput(
  tokenAmount: bigint,
  state: LaunchLabPoolState
): bigint {
  if (state.graduated) {
    throw new Error('Pool has graduated - use Raydium AMM or Jupiter');
  }

  const numerator = tokenAmount * state.quoteReserve;
  const denominator = state.tokenReserve + tokenAmount;

  return numerator / denominator;
}

/**
 * Apply slippage
 */
export function applySlippage(amount: bigint, slippageBps: number): bigint {
  return (amount * BigInt(10000 - slippageBps)) / BigInt(10000);
}

/**
 * Build buy instruction
 */
export async function buildBuyInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  solAmount: bigint;
  minTokensOut: bigint;
}): Promise<TransactionInstruction> {
  const { mint, userWallet, solAmount, minTokensOut } = params;

  const pool = getPoolPDA(mint);
  const tokenVault = getPoolTokenVault(mint);
  const solVault = getPoolSolVault(mint);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // Buy instruction discriminator
  const BUY_DISCRIMINATOR = Buffer.from([0x66, 0x06, 0x3d, 0x12, 0x01, 0xda, 0xeb, 0xea]);

  const data = Buffer.alloc(24);
  BUY_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(solAmount, 8);
  data.writeBigUInt64LE(minTokensOut, 16);

  return new TransactionInstruction({
    programId: RAYDIUM_LAUNCHLAB_PROGRAM_ID,
    keys: [
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: tokenVault, isSigner: false, isWritable: true },
      { pubkey: solVault, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ASSOCIATED_TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Build sell instruction
 */
export async function buildSellInstruction(params: {
  mint: PublicKey;
  userWallet: PublicKey;
  tokenAmount: bigint;
  minSolOut: bigint;
}): Promise<TransactionInstruction> {
  const { mint, userWallet, tokenAmount, minSolOut } = params;

  const pool = getPoolPDA(mint);
  const tokenVault = getPoolTokenVault(mint);
  const solVault = getPoolSolVault(mint);
  const userTokenAccount = await getAssociatedTokenAddress(mint, userWallet);

  // Sell instruction discriminator
  const SELL_DISCRIMINATOR = Buffer.from([0x33, 0xe6, 0x85, 0xa4, 0x01, 0x7f, 0x83, 0xad]);

  const data = Buffer.alloc(24);
  SELL_DISCRIMINATOR.copy(data, 0);
  data.writeBigUInt64LE(tokenAmount, 8);
  data.writeBigUInt64LE(minSolOut, 16);

  return new TransactionInstruction({
    programId: RAYDIUM_LAUNCHLAB_PROGRAM_ID,
    keys: [
      { pubkey: pool, isSigner: false, isWritable: true },
      { pubkey: mint, isSigner: false, isWritable: false },
      { pubkey: tokenVault, isSigner: false, isWritable: true },
      { pubkey: solVault, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userWallet, isSigner: true, isWritable: true },
      { pubkey: SystemProgram.programId, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
    ],
    data,
  });
}

/**
 * Get buy quote
 */
export async function getBuyQuote(
  connection: Connection,
  mint: PublicKey,
  solAmount: bigint,
  slippageBps: number
): Promise<{
  expectedTokens: bigint;
  minTokensOut: bigint;
  priceImpact: number;
  isGraduated: boolean;
}> {
  const state = await getPoolState(connection, mint);

  if (!state) {
    throw new Error('Token not found on Raydium LaunchLab');
  }

  if (state.graduated) {
    return {
      expectedTokens: BigInt(0),
      minTokensOut: BigInt(0),
      priceImpact: 0,
      isGraduated: true,
    };
  }

  const expectedTokens = calculateBuyOutput(solAmount, state);
  const minTokensOut = applySlippage(expectedTokens, slippageBps);

  // Calculate price impact
  const priceBefore = Number(state.quoteReserve) / Number(state.tokenReserve);
  const priceAfter = Number(state.quoteReserve + solAmount) /
                     Number(state.tokenReserve - expectedTokens);
  const priceImpact = ((priceAfter - priceBefore) / priceBefore) * 100;

  return {
    expectedTokens,
    minTokensOut,
    priceImpact,
    isGraduated: false,
  };
}

/**
 * Get sell quote
 */
export async function getSellQuote(
  connection: Connection,
  mint: PublicKey,
  tokenAmount: bigint,
  slippageBps: number
): Promise<{
  expectedSol: bigint;
  minSolOut: bigint;
  priceImpact: number;
  isGraduated: boolean;
}> {
  const state = await getPoolState(connection, mint);

  if (!state) {
    throw new Error('Token not found on Raydium LaunchLab');
  }

  if (state.graduated) {
    return {
      expectedSol: BigInt(0),
      minSolOut: BigInt(0),
      priceImpact: 0,
      isGraduated: true,
    };
  }

  const expectedSol = calculateSellOutput(tokenAmount, state);
  const minSolOut = applySlippage(expectedSol, slippageBps);

  const priceBefore = Number(state.quoteReserve) / Number(state.tokenReserve);
  const priceAfter = Number(state.quoteReserve - expectedSol) /
                     Number(state.tokenReserve + tokenAmount);
  const priceImpact = ((priceBefore - priceAfter) / priceBefore) * 100;

  return {
    expectedSol,
    minSolOut,
    priceImpact,
    isGraduated: false,
  };
}

/**
 * Check if token is on Raydium LaunchLab
 */
export async function isLaunchLabToken(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const state = await getPoolState(connection, mint);
  return state !== null;
}

/**
 * Check if token has graduated
 */
export async function isGraduated(
  connection: Connection,
  mint: PublicKey
): Promise<boolean> {
  const state = await getPoolState(connection, mint);
  return state?.graduated ?? false;
}
